import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError as observableThrowError } from 'rxjs';
import {
    IHeadCountTotals,
    IOverallScorecardKPI,
    IScoreCardGrades,
    IProjectionYTDActuals,
    IProjectionsUser,
    IProjectionsUserParameters,
    IProjectionsToActualsLargeCardParameters,
    IProjectionsToActualsLargeCard,
    IProjectionsToActualsTabularListByPosition,
} from '../interfaces/performance-data';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { IRowItems, ITableTdItems } from 'app/modules/shared/interfaces/card-data';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
    providedIn: 'root',
})
export class PerformanceService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'performance';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private http: HttpClient,
        private reusableFunctions: ReusableFunctions,
        private userAccessService: UserAccessService,
        private sanitizer: DomSanitizer
    ) {}

    getPerformanceHeadCountTotals(
        fromDatePeriod: string,
        headCountType: string,
        hFilterJSON: string
    ): Observable<IHeadCountTotals[]> {
        return this.http.post<IHeadCountTotals[]>(this._url + '/GetPerformanceHeadCountTotals', {
            FromDatePeriod: fromDatePeriod,
            HeadCountType: headCountType,
            HFilterJSON: hFilterJSON,
        });
    }

    getScoreCardGrades(
        fromDatePeriod: string,
        grade: string,
        range: string,
        rangeType: string,
        rangeValue: number,
        tenuredOnly: boolean,
        hFilterJSON: string
    ): Observable<IScoreCardGrades[]> {
        return this.http.post<IScoreCardGrades[]>(this._url + '/GetScoreCardGrades', {
            FromDatePeriod: fromDatePeriod,
            Grade: grade,
            Range: range,
            RangeType: rangeType,
            RangeValue: rangeValue,
            TenuredOnly: tenuredOnly,
            HFilterJSON: hFilterJSON,
        });
    }

    getOverallScorecardKPI(
        fromDatePeriod: string,
        hFilterJSON: string
    ): Observable<IOverallScorecardKPI[]> {
        return this.http.post<IOverallScorecardKPI[]>(this._url + '/GetOverallScorecardKPI', {
            FromDatePeriod: fromDatePeriod,
            HFilterJSON: hFilterJSON,
        });
    }

    getProjectionYTDActuals(
        fromDatePeriod: string,
        hFilterJSON: string
    ): Observable<IProjectionYTDActuals[]> {
        return this.http.post<IProjectionYTDActuals[]>(this._url + '/GetProjectionYTDActuals', {
            FromDatePeriod: fromDatePeriod,
            HFilterJSON: hFilterJSON,
        });
    }

    setPerformanceAAGData(
        displayOptions: DisplayOptions[],
        hFilterJson: string,
        cardInfo: ICardInfo,
        action: NoDataActionType,
        isProjection: boolean = false
    ) {
        if (isProjection) {
            this.getProjectionYTDActuals('Last Month', hFilterJson).subscribe(
                (data: IProjectionYTDActuals[]) => {
                    this.handleProjectionLargeCard(data, action, cardInfo);
                },
                () => {
                    this.handleProjectionLargeCard([], NoDataActionType.error, cardInfo);
                }
            );
        } else {
            const fromDatePeriod =
                displayOptions.find((item) => item.optionKey === 'dateRangeYtd')?.selectedOptions ??
                'This Year';

            this.getOverallScorecardKPI(fromDatePeriod, hFilterJson).subscribe(
                (data: IOverallScorecardKPI[]) => {
                    this.handleKPILargeCards(data, action, cardInfo);
                },
                () => {
                    this.handleKPILargeCards([], NoDataActionType.error, cardInfo);
                }
            );
        }
    }

    handleProjectionLargeCard(
        data: IProjectionYTDActuals[],
        action: NoDataActionType,
        cardInfo: ICardInfo
    ) {
        interface IProjectionTableInfo extends ITableTdItems {
            precisionColId?: string;
            formatColId?: string;
        }

        let projectionTableInfo: IProjectionTableInfo[] = [
            {
                headerTitle: 'Metric',
                fieldId: 'description',
                isHyperlinked: true,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'left',
                    color: '#333333',
                },
                style: {
                    'font-weight': '500',
                    'text-align': 'left',
                    'font-size': '14px',
                    width: '26%',
                },
            },
            {
                headerTitle: 'Projected',
                fieldId: 'ytdProj',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '24%',
                },
                precisionColId: 'ytdProjPrecision',
                formatColId: 'ytdProjFormat',
            },
            {
                headerTitle: 'Actual',
                fieldId: 'ytdActual',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '22%',
                },
                precisionColId: 'ytdActualPrecision',
                formatColId: 'ytdActualFormat',
            },
            {
                headerTitle: '% Complete',
                fieldId: 'ytdPct',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '24%',
                },
            },
        ];

        let hasData = false;
        if (data.length > 0) {
            hasData = true;

            let tableRowItems: IRowItems[] = [];
            data.forEach((item) => {
                let dataItem = Object.assign({}, item);
                let tableTdItems = [];
                if (projectionTableInfo) {
                    projectionTableInfo.forEach((rowInf) => {
                        let rowInfCopy = Object.assign({}, rowInf);
                        let value = dataItem[rowInfCopy.fieldId];

                        if (value != null && typeof value === 'number') {
                            rowInfCopy.precision =
                                dataItem[rowInfCopy.precisionColId] != undefined
                                    ? dataItem[rowInfCopy.precisionColId]
                                    : 2;
                            rowInfCopy.dataType =
                                dataItem[rowInfCopy.formatColId] != undefined
                                    ? dataItem[rowInfCopy.formatColId]
                                    : 'number';
                            if (rowInfCopy.dataType === 'currency') {
                                value = this.reusableFunctions.formatMoney(
                                    dataItem[rowInfCopy.fieldId],
                                    rowInfCopy.precision
                                );
                            } else {
                                let isNegative = value < 0;

                                if (isNegative) {
                                    value = Math.abs(value);
                                }
                                value = value.toLocaleString(undefined, {
                                    maximumFractionDigits: rowInfCopy.precision,
                                    minimumFractionDigits: rowInfCopy.precision,
                                });

                                if (rowInfCopy.dataType === 'percentage') {
                                    value = value + '%';
                                }

                                if (isNegative) {
                                    value = '(' + value + ')';
                                    rowInfCopy.indicatorColor =
                                        rowInfCopy.headerTitle === '% Complete' ? '#E0000E' : '';
                                } else {
                                    rowInfCopy.indicatorColor =
                                        rowInfCopy.headerTitle === '% Complete' ? '#008800' : '';
                                }
                            }
                        }

                        rowInfCopy.value = value;
                        if (rowInfCopy.fieldId === 'description') {
                            rowInfCopy.urlSegment =
                                cardInfo.drilldownSegment +
                                '_' +
                                dataItem.description.replace(/ /g, '');
                            rowInfCopy.routerLink = cardInfo.routerPath;
                            rowInfCopy.queryParams = {
                                description: dataItem.description,
                            };
                        }

                        if (rowInfCopy.isHyperlinked) {
                            rowInfCopy.hasAccess = this.userAccessService.hasAccessToPage(
                                rowInfCopy.routerLink + rowInfCopy.urlSegment
                            );
                        }
                        tableTdItems.push(rowInfCopy);
                    });

                    tableRowItems.push({
                        tdList: tableTdItems,
                    });
                }
            });
            cardInfo.dataTableData = tableRowItems;
        }
        cardInfo.hasData = hasData;
        cardInfo.isDataReady = true;
        cardInfo.action = action;
        cardInfo = Object.assign({}, cardInfo);
    }

    handleKPILargeCards(data: IOverallScorecardKPI[], action: NoDataActionType, cardInfo) {
        let kpiTableInfo: ITableTdItems[] = [
            {
                headerTitle: 'KPI',
                fieldId: 'clientKPIMatrixDescription',
                isHyperlinked: true,
                headerStyle: {
                    'text-align': 'left',
                    color: '#333333',
                    'text-transform': 'uppercase',
                },
                style: {
                    'font-weight': '500',
                    'text-align': 'left',
                    'font-size': '14px',
                    width: '25%',
                },
            },
            {
                headerTitle: 'Target',
                fieldId: 'target',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '14%',
                },
            },
            {
                headerTitle: 'MTD',
                fieldId: 'monthlyValue',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '15%',
                },
            },
            {
                headerTitle: 'Prior MTD',
                fieldId: 'monthlyValue',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '16%',
                },
                showIndicator: true,
                compareFieldId: 'previousMonthlyValue',
                diffField: 'monthlyDiff',
            },
            {
                headerTitle: 'YTD',
                fieldId: 'ytdValue',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '15%',
                },
            },
            {
                headerTitle: 'Prior YTD',
                fieldId: 'ytdValue',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '17%',
                },
                showIndicator: true,
                compareFieldId: 'previousYTDValue',
                diffField: 'ytdDiff',
            },
        ];

        let hasData = false;
        if (data.length > 0) {
            hasData = true;

            let tableRowItems: IRowItems[] = [];
            data.forEach((item) => {
                let dataItem = Object.assign({}, item);
                let tableTdItems = [];
                if (kpiTableInfo) {
                    kpiTableInfo.forEach((rowInf) => {
                        let rowInfCopy = Object.assign({}, rowInf);
                        let value = dataItem[rowInfCopy.fieldId];

                        if (value != null && typeof value === 'number') {
                            if (rowInfCopy.showIndicator) {
                                let comparisonInf =
                                    this.reusableFunctions.calculateComparisonPercentage(
                                        dataItem[rowInfCopy.fieldId],
                                        dataItem[rowInfCopy.compareFieldId]
                                    );

                                let caretInfoMonthly = this.reusableFunctions.getCaret(
                                    comparisonInf.percentage,
                                    dataItem.bestScore === 'Lowest' ? true : false,
                                    null,
                                    comparisonInf.isComparable,
                                    2
                                );
                                rowInfCopy.indicatorIcon = caretInfoMonthly.ng_caret_icon;
                                rowInfCopy.svgData = this.sanitizer.bypassSecurityTrustHtml(
                                    caretInfoMonthly.svgData
                                );
                                rowInfCopy.indicatorColor = caretInfoMonthly.color;
                                value = caretInfoMonthly.comparison;
                            } else if (
                                dataItem.format === 'currency' &&
                                (rowInfCopy.headerTitle === 'MTD' ||
                                    rowInfCopy.headerTitle === 'YTD' ||
                                    rowInfCopy.fieldId.toLowerCase() === 'target')
                            ) {
                                value = this.reusableFunctions.formatMoney(
                                    dataItem[rowInfCopy.fieldId],
                                    dataItem.precision
                                );
                            } else {
                                let isNegative = value < 0;

                                if (isNegative) {
                                    value = Math.abs(value);
                                }

                                value = value.toLocaleString(undefined, {
                                    maximumFractionDigits: dataItem.precision,
                                    minimumFractionDigits: dataItem.precision,
                                });

                                if (dataItem.format === 'percentage') {
                                    value = value + '%';
                                }

                                if (isNegative) {
                                    value = '(' + value + ')';
                                }
                            }
                        }
                        rowInfCopy.value = value;

                        if (rowInfCopy.isHyperlinked) {
                            rowInfCopy.urlSegment = '';
                            rowInfCopy.routerLink = '';
                            rowInfCopy.hasAccess = this.userAccessService.hasAccessToPage(
                                rowInfCopy.routerLink + rowInfCopy.urlSegment
                            );
                        }
                        tableTdItems.push(rowInfCopy);
                    });

                    tableRowItems.push({
                        tdList: tableTdItems,
                    });
                }
            });

            cardInfo.dataTableData = tableRowItems;
        }

        cardInfo.hasData = hasData;
        cardInfo.isDataReady = true;
        cardInfo.action = action;
        cardInfo = Object.assign({}, cardInfo);
    }

    getRankingAndScoreTableInfo() {
        let rankTableInfo: ITableTdItems[] = [
            {
                headerTitle: '',
                fieldId: 'shortName',
                isHyperlinked: true,
                routerLink: '',
                hyperlinkFieldId: 'empId',
                headerStyle: {
                    width: '30%',
                },
                style: {
                    'font-weight': '500',
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '30%',
                },
            },
            {
                headerTitle: 'Units',
                fieldId: 'fundedUnits',
                isHyperlinked: false,
                headerStyle: {
                    width: '15%',
                    'text-align': 'right',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '15%',
                },
                precision: 0,
            },
            {
                headerTitle: 'Volume',
                fieldId: 'fundedVol',
                isHyperlinked: false,
                headerStyle: {
                    width: '25%',
                    'text-align': 'right',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '25%',
                },
                dataType: 'currency',
                precision: 0,
            },
            {
                headerTitle: 'Tenure',
                fieldId: 'tenure',
                isHyperlinked: false,
                headerStyle: {
                    width: '30%',
                    'text-align': 'right',
                    'padding-right': '5px',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '30%',
                    'padding-right': '5px',
                },
            },
        ];

        let scoreTableInfo: ITableTdItems[] = [
            {
                headerTitle: '',
                fieldId: 'shortName',
                isHyperlinked: true,
                hyperlinkFieldId: 'empId',
                headerStyle: {
                    width: '25%',
                },
                style: {
                    'font-weight': '500',
                    'font-size': '13px',
                    'text-align': 'right',
                    width: '25%',
                },
            },
            {
                headerTitle: 'Score',
                fieldId: 'score',
                isHyperlinked: false,
                headerStyle: {
                    width: '15%',
                    'text-align': 'right',
                    'padding-right': '4px',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '15%',
                },
                precision: 2,
            },
            {
                headerTitle: 'Units',
                fieldId: 'fundedUnits',
                isHyperlinked: false,
                headerStyle: {
                    width: '15%',
                    'text-align': 'right',
                    'padding-right': '4px',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '15%',
                },
                precision: 0,
            },
            {
                headerTitle: 'Volume',
                fieldId: 'fundedVol',
                isHyperlinked: false,
                headerStyle: {
                    width: '25%',
                    'text-align': 'right',
                    'padding-right': '4px',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '25%',
                },
                dataType: 'currency',
                precision: 0,
            },
            {
                headerTitle: 'Tenure',
                fieldId: 'tenure',
                isHyperlinked: false,
                headerStyle: {
                    width: '20%',
                    'text-align': 'right',
                    'padding-right': '8px',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '13px',
                    width: '75px',
                    'padding-right': '5px',
                },
            },
        ];

        return {
            rankTableInfo,
            scoreTableInfo,
        };
    }

    getProjectionsUserList(parameters: IProjectionsUserParameters): Observable<IProjectionsUser[]> {
        return this.http.post<IProjectionsUser[]>(
            this._url + '/GetProjectionsUserList',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getProjectionsToActualsLargeCard(
        parameters: IProjectionsToActualsLargeCardParameters
    ): Observable<IProjectionsToActualsLargeCard[]> {
        return this.http.post<IProjectionsToActualsLargeCard[]>(
            this._url + '/GetProjectionsToActualsLargeCard',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getProjectionsToActualsTabularListByPosition(
        parameters: IProjectionsToActualsLargeCardParameters
    ): Observable<IProjectionsToActualsTabularListByPosition[]> {
        return this.http.post<IProjectionsToActualsTabularListByPosition[]>(
            this._url + '/GetProjectionsToActualsTabularListByPosition',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }
}
